import xeDesignSystemVisuals from "../../media/xeDesignSystemVisuals";

const FEATURED_VISUAL_INDEXES = new Set([0, 1, 5, 7, 14]);

const designSystem = {
  "id": "design-system",
  "slug": "design-system",
  "category": "Foundations",
  "navTitle": "Building XE’s Design System",
  "title": "Building XE’s Design System",
  "introduction": [
    "As XE’s website and mobile products grew, teams were repeatedly designing and building similar interface elements. Differences between platforms made the experience less consistent and created extra work for designers and developers.",
    "I led the creation of a shared design system that brought reusable components, accessibility standards, documentation, and clearer handoffs into the product workflow."
  ],
  "challenge": [
    {
      "title": "Inconsistent experiences",
      "paragraphs": [
        "Web and mobile products had developed at different times and often used different visual and interaction patterns. Customers could move between XE products and encounter noticeably different experiences."
      ]
    },
    {
      "title": "Repeated design and development work",
      "paragraphs": [
        "Without shared components, teams often recreated common interface elements for each project. This slowed delivery and made updates harder to coordinate."
      ]
    },
    {
      "title": "Accessibility required too much manual effort",
      "paragraphs": [
        "Accessibility depended heavily on individual teams identifying and correcting issues. Common requirements needed to be built into the components teams used every day."
      ]
    },
    {
      "title": "Different tools and ways of working",
      "paragraphs": [
        "Designers and developers lacked a shared source of truth. Files, specifications, and implemented components could drift apart as products evolved."
      ]
    }
  ],
  "process": [
    {
      "title": "Creating a shared foundation",
      "paragraphs": [
        "I began by identifying the interface patterns being used repeatedly across XE’s products and defining where greater consistency would provide the most value."
      ],
      "bullets": [
        "Reviewed existing web and mobile experiences to identify duplicated components and inconsistencies.",
        "Worked with product, design, and engineering teams to define the initial scope.",
        "Established shared principles for typography, colour, spacing, interaction, and accessibility.",
        "Used atomic design principles to organize components from basic elements into larger patterns.",
        "Prioritized components that appeared frequently and could reduce repeated work quickly."
      ],
      "closing": "Starting with common product needs allowed the system to grow from practical use rather than trying to define everything in advance."
    },
    {
      "title": "Connecting design and development",
      "paragraphs": [
        "I created corresponding design and coded component libraries so teams could work from the same foundation."
      ],
      "bullets": [
        "Built a React component library for the website.",
        "Created a shared UI kit in Sketch, which was later moved into Figma.",
        "Used Zeplin to improve specifications and handoffs between design and development.",
        "Documented component behaviour, usage, states, and accessibility requirements.",
        "Worked with developers to keep designed and implemented components aligned."
      ],
      "closing": "This gave designers a clearer understanding of what was already available and gave developers more reliable specifications."
    },
    {
      "title": "Supporting adoption and continued use",
      "paragraphs": [
        "I treated the system as an ongoing product that needed ownership, feedback, and regular maintenance."
      ],
      "bullets": [
        "Introduced the system through workshops and direct support for design and development teams.",
        "Used feedback from active projects to improve components and documentation.",
        "Established versioning and release notes so teams could understand what had changed.",
        "Added new components as recurring product needs emerged.",
        "Extended the system across website and mobile work while allowing for platform-specific patterns where needed."
      ],
      "closing": "Adoption grew through use. Each project helped identify where the system was working and where it needed to improve."
    }
  ],
  "outcomes": [
    {
      "title": "Greater consistency across products",
      "description": "Shared components and standards created a more recognizable experience across XE’s website and mobile platforms."
    },
    {
      "title": "Faster design and development",
      "description": "Teams could reuse established patterns instead of designing and building common elements repeatedly."
    },
    {
      "title": "Accessibility built into shared components",
      "description": "Common accessibility requirements were addressed within the system, reducing the amount of manual correction required within individual projects."
    },
    {
      "title": "Clearer collaboration",
      "description": "The design library, coded components, documentation, and release information gave design and engineering teams a stronger shared reference."
    },
    {
      "title": "A foundation for continued product growth",
      "description": "The system supported XE as its products, services, and platforms expanded, while making future improvements easier to apply consistently."
    }
  ],
  "reflection": [
    "Building XE’s design system showed me how quickly inconsistency develops when teams solve the same problems separately.",
    "The system gave us a practical way to share decisions across products and disciplines. Its usefulness depended on staying close to the work, listening to the teams using it, and continuing to update it as XE’s needs changed."
  ],
  "media": {
    "carousel": {
      "items": xeDesignSystemVisuals.filter((_, index) =>
        FEATURED_VISUAL_INDEXES.has(index)
      ),
      "layout": "desktopWide",
      "options": {
        "frameMaxHeight": "min(44vh, 460px)"
      }
    },
    "grid": {
      "title": "Design System Gallery",
      "items": xeDesignSystemVisuals,
      "layout": "default",
      "options": {
        "thumbHeight": 260,
        "thumbFit": "cover"
      }
    }
  }
};

export default designSystem;
